"use client"

import styled from "@emotion/styled"
import { AnalyticsObject } from "app/analytics/utils/generateAnalyticsObject"
import getAnalyticsUserGroups from "app/analytics/utils/getAnalyticsUserGroups"
import Footer from "app/footer/components/Footer"
import Header from "app/header/components/Header"
import { MODAL_QUERY_ARCHIVE_KEY, MODAL_QUERY_KEY } from "app/playlists/consts"

import SupportPopover from "app/support/components/SupportPopover"
import { discoveryModalOpenAtom } from "app/support/discoveryModalState"
import { GetCurrentUserResult } from "app/users/queries/getCurrentUser"
import { SupportContentType } from "db/enums"
import { useAtom } from "jotai"
import dynamic from "next/dynamic"
import { useRouter } from "app/core/hooks/useRouter"
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs"
import {
  PropsWithChildren,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react"
import Stack from "ui/Stack"
import MaintenanceBar from "../components/MaintenanceBar"
import { SUPPORT_LINKS } from "../consts"
import { useCurrentGroup } from "../hooks/useCurrentGroup"
import { useCurrentUser } from "../hooks/useCurrentUser"
import useIdentifyUser from "../hooks/useIdentifyUser"
import { usePosthogWithCurrentUser } from "../hooks/usePosthogWithCurrentUser"
import { useMaintenanceFromContext } from "../providers/SystemInfoProvider"
import Layout from "./Layout"
import { useSearchParams } from "next/navigation"
import { css, Global } from "@emotion/react"

const DiscoveryModal = dynamic(
  () => import("app/support/components/DiscoveryModal")
)
const PlaylistModal = dynamic(
  () => import("app/playlists/components/PlaylistModal")
)
const ArchiveModal = dynamic(
  () => import("app/schooljaren/components/ArchiveModal")
)

const Root = styled.div`
  /* padding-bottom: 100px; */
`

export const StackWithBackground = styled(Stack)<{ backgroundColor?: string }>`
  background-color: ${(p) =>
    p.backgroundColor ? p.backgroundColor : undefined};
`

export default function DashboardLayout(
  props: PropsWithChildren<{
    title?: string | null
    type?: keyof typeof SUPPORT_LINKS
    backgroundColor?: string
    hideFooter?: boolean
    disablePadding?: boolean
    analyticsObject?: Partial<AnalyticsObject>
    supportContentType?: SupportContentType
    suppressHomeModal?: (state: "MINIMIZED" | "CLOSED") => void
    stateHomeModal?: "OPENED" | "MINIMIZED" | "CLOSED"
  }>
) {
  const currentUser = useCurrentUser()
  const router = useRouter()

  const [showWelcomeTour, setShowWelcomeTour] = useQueryState(
    "showWelcomeTour",
    parseAsBoolean
  )

  const [discoveryModalOpen, setDiscoveryModalOpen] = useAtom(
    discoveryModalOpenAtom
  )

  const { analyticsObject: analyticsObjectInit } = props

  const [isSupportButtonOpen, setIsSupportButtonOpen] = useState(false)

  const maintenance = useMaintenanceFromContext()

  const [queryParam, setOpen] = useQueryState(MODAL_QUERY_KEY, parseAsString)
  const hasParam = queryParam !== null
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useQueryState(
    MODAL_QUERY_ARCHIVE_KEY
  )

  const group = useCurrentGroup()
  const analytics = usePosthogWithCurrentUser(
    currentUser as GetCurrentUserResult
  )

  const [isPlaylistModalLoaded, setIsPlaylistModalLoaded] = useState(false)

  useEffect(() => {
    if (showWelcomeTour) {
      if (!discoveryModalOpen) {
        analytics.capture({
          name: "discovery_modal_opened",
        })
      }
      setDiscoveryModalOpen(true)
    }
  }, [showWelcomeTour, setDiscoveryModalOpen, analytics, discoveryModalOpen])

  useEffect(() => {
    if (
      currentUser &&
      currentUser.organizationUsers &&
      currentUser.organizationUsers.length > 0
    ) {
      if (currentUser.organizationUsers[0].onboardingType === "ONBOARDING") {
        router.push(
          router.query.next
            ? `/onboarding/welkom?next=${encodeURIComponent(router.query.next as string)}`
            : "/onboarding/welkom"
        )
      } else if (
        currentUser.organizationUsers[0].onboardingType === "SCHOOLYEAR"
      ) {
        router.push(
          `/reboarding/welkom${router.query.next && "/reboarding/welkom" !== router.query.next ? `?next=${encodeURIComponent(router.query.next as string)}` : ""}`
        )
      }
    }
  }, [router, currentUser])

  const analyticsObject = useMemo<
    Partial<Omit<AnalyticsObject, "userType" | "subscriptionType">>
  >(() => {
    return {
      ...analyticsObjectInit,
      userType: currentUser?.role,
      ...getAnalyticsUserGroups(group?.grades),
    }
  }, [analyticsObjectInit, currentUser, group])

  useIdentifyUser(currentUser)

  return (
    <Layout {...props} analyticsObject={analyticsObject}>
      <Global
        styles={css`
          body {
            color: var(--color-neutraal-900);
          }
        `}
      />
      <MaintenanceBar props={maintenance} />

      <Root className="text-neutraal-900">
        {props.supportContentType && (
          <SupportPopover
            supportContentType={props.supportContentType}
            isSupportButtonOpen={isSupportButtonOpen}
            setIsSupportButtonOpen={setIsSupportButtonOpen}
            currentUser={currentUser}
          />
        )}

        {discoveryModalOpen && (
          <DiscoveryModal
            showDiscoveryModal={discoveryModalOpen}
            setShowDiscoveryModal={setDiscoveryModalOpen}
            openWelcomeTour={!!showWelcomeTour}
            openSchoolManagementTour={false}
            setOpenWelcomeTour={setShowWelcomeTour}
            setOpenSchoolManagementTour={() => false}
            currentUser={currentUser}
            stateHomeModal={props.stateHomeModal}
            suppressHomeModal={props.suppressHomeModal}
          />
        )}

        {/* </Suspense>
        )} */}
        <Header
          setIsPlaylistModalLoaded={setIsPlaylistModalLoaded}
          group={group}
        />
        <Suspense>
          {(isPlaylistModalLoaded || hasParam) && <PlaylistModal />}
        </Suspense>

        {isArchiveModalOpen !== null && <ArchiveModal />}

        <StackWithBackground
          gap={props.disablePadding ? "0px" : "100px"}
          backgroundColor={props.backgroundColor}
        >
          <div>{props.children}</div>
          {!props.hideFooter && <Footer />}
        </StackWithBackground>
      </Root>
    </Layout>
  )
}
DashboardLayout.suppressFirstRenderFlicker = true
